import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { del, get, post, put } from '../../api/request';
import { API_BASE_URL, API_ENDPOINTS } from '../../api/config';
import HlsMedia from '../../components/HlsMedia';
import './BackgroundMusicPage.css';

const { TextArea } = Input;

interface BackgroundMusic {
  id: number;
  title: string;
  category: string;
  audio_url: string;
  source_url: string;
  license_name: string;
  artist: string;
  duration_seconds: number;
  volume: number;
  sort: number;
  status: number;
  description: string;
  updated_at?: string;
}

const categoryOptions = [
  { label: '雨声', value: 'rain' },
  { label: '溪流', value: 'stream' },
  { label: '钢琴', value: 'piano' },
  { label: '环境音乐', value: 'ambient' },
  { label: '白噪音', value: 'noise' },
  { label: '其他', value: 'other' },
];

const categoryLabel = (value: string) => categoryOptions.find((item) => item.value === value)?.label || value;

const BackgroundMusicPage: React.FC = () => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<BackgroundMusic[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<BackgroundMusic | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [uploadForm] = Form.useForm();
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await get<BackgroundMusic[]>(API_ENDPOINTS.storage.backgroundMusic);
      setItems(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载背景音乐失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      title: '',
      category: 'relax',
      audio_url: '',
      source_url: '',
      license_name: '',
      artist: '',
      duration_seconds: 0,
      volume: 30,
      sort: 100,
      status: 1,
      description: '',
    });
    setModalOpen(true);
  };

  const openUpload = () => {
    uploadForm.setFieldsValue({
      title: '',
      category: 'other',
      license_name: '手动上传',
      artist: '',
      duration_seconds: 0,
      volume: 30,
      sort: 100,
      status: 1,
      description: '',
    });
    setUploadFiles([]);
    setUploadModalOpen(true);
  };

  const openEdit = (record: BackgroundMusic) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await put(API_ENDPOINTS.storage.backgroundMusicDetail(editing.id), values);
      } else {
        await post(API_ENDPOINTS.storage.backgroundMusic, values);
      }
      message.success('背景音乐已保存');
      setModalOpen(false);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存背景音乐失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (record: BackgroundMusic, checked: boolean) => {
    try {
      await put(API_ENDPOINTS.storage.backgroundMusicDetail(record.id), { status: checked ? 1 : 0 });
      message.success(checked ? '已启用' : '已停用');
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新状态失败');
    }
  };

  const deleteItem = async (record: BackgroundMusic) => {
    try {
      await del(API_ENDPOINTS.storage.backgroundMusicDetail(record.id));
      message.success('背景音乐已删除');
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除背景音乐失败');
    }
  };

  const batchDelete = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先选择要删除的背景音乐');
      return;
    }
    try {
      const result = await post<{ deleted: number }>(API_ENDPOINTS.storage.backgroundMusicBatchDelete, {
        ids: selectedRowKeys.map((item) => Number(item)),
      });
      message.success(`已删除 ${result.deleted} 条背景音乐`);
      setSelectedRowKeys([]);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '批量删除失败');
    }
  };

  const handleUpload = async () => {
    const values = await uploadForm.validateFields();
    const file = uploadFiles[0]?.originFileObj;
    if (!file) {
      message.warning('请选择要上传的音频文件');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        formData.append(key, String(value ?? ''));
      });
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.storage.backgroundMusicUpload}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `上传失败: ${response.status}`);
      }
      message.success('背景音乐已上传');
      setUploadModalOpen(false);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '上传背景音乐失败');
    } finally {
      setUploading(false);
    }
  };

  const columns: ColumnsType<BackgroundMusic> = [
    {
      title: '音乐',
      dataIndex: 'title',
      width: 560,
      render: (_, record) => (
        <div className="bgm-title-cell">
          <strong>{record.title}</strong>
          <span>{record.description || record.artist || '暂无描述'}</span>
        </div>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 110,
      render: (value) => <Tag color="blue">{categoryLabel(value)}</Tag>,
    },
    {
      title: '试听',
      dataIndex: 'audio_url',
      width: 320,
      render: (value, record) => (
        <HlsMedia className="bgm-audio" src={value} controls preload="none" />
      ),
    },
    {
      title: '来源/授权',
      width: 220,
      render: (_, record) => (
        <div className="bgm-source-cell">
          <span>{record.artist || '-'}</span>
          <Tag>{record.license_name || '未填写'}</Tag>
        </div>
      ),
    },
    {
      title: '音量',
      dataIndex: 'volume',
      width: 90,
      render: (value) => `${value}%`,
    },
    {
      title: '启用',
      dataIndex: 'status',
      width: 90,
      render: (value, record) => <Switch checked={value === 1} onChange={(checked) => toggleStatus(record, checked)} />,
    },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!record.audio_url}
            href={record.audio_url || undefined}
            target="_blank"
            download
          >
            下载
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除这条背景音乐？" onConfirm={() => deleteItem(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="background-music-page">
      <div className="bgm-header">
        <div>
          <h1 className="page-title">背景音乐库</h1>
          <p>管理核心功能测试和后续前台可使用的舒缓背景音乐。</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchItems}>刷新</Button>
          <Button icon={<UploadOutlined />} onClick={openUpload}>上传音乐</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增音乐</Button>
        </Space>
      </div>

      <Card bordered={false}>
        <div className="bgm-toolbar">
          <Space>
            <span>已选择 {selectedRowKeys.length} 条</span>
            <Popconfirm title={`确认删除选中的 ${selectedRowKeys.length} 条背景音乐？`} onConfirm={batchDelete} disabled={!selectedRowKeys.length}>
              <Button danger icon={<DeleteOutlined />} disabled={!selectedRowKeys.length}>
                批量删除
              </Button>
            </Popconfirm>
          </Space>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{ pageSize: 10 }}
          tableLayout="fixed"
          scroll={{ x: 1450 }}
        />
      </Card>

      <Modal
        title={editing ? '编辑背景音乐' : '新增背景音乐'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={760}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <div className="bgm-form-grid">
            <Form.Item label="名称" name="title" rules={[{ required: true, message: '请输入名称' }]}>
              <Input placeholder="例如 窗外细雨" />
            </Form.Item>
            <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择分类' }]}>
              <Select options={categoryOptions} />
            </Form.Item>
          </div>

          <Form.Item label="音频 URL" name="audio_url" rules={[{ required: true, message: '请输入音频 URL' }]}>
            <Input placeholder="https://..." />
          </Form.Item>

          <div className="bgm-form-grid">
            <Form.Item label="来源 URL" name="source_url">
              <Input placeholder="来源页面地址" />
            </Form.Item>
            <Form.Item label="授权" name="license_name">
              <Input placeholder="Public domain / CC BY 3.0" />
            </Form.Item>
          </div>

          <div className="bgm-form-grid">
            <Form.Item label="作者" name="artist">
              <Input placeholder="作者或来源名称" />
            </Form.Item>
            <Form.Item label="时长（秒）" name="duration_seconds">
              <InputNumber min={0} max={86400} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="bgm-form-grid">
            <Form.Item label="默认音量（%）" name="volume" rules={[{ required: true, message: '请输入默认音量' }]}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="排序" name="sort" rules={[{ required: true, message: '请输入排序' }]}>
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
            <Select options={[{ label: '启用', value: 1 }, { label: '停用', value: 0 }]} />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="适用场景、使用建议" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="上传背景音乐"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onOk={handleUpload}
        confirmLoading={uploading}
        width={760}
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item label="音频文件" required>
            <Upload
              accept="audio/*"
              maxCount={1}
              fileList={uploadFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setUploadFiles(fileList)}
            >
              <Button icon={<UploadOutlined />}>选择音频文件</Button>
            </Upload>
          </Form.Item>

          <div className="bgm-form-grid">
            <Form.Item label="名称" name="title" rules={[{ required: true, message: '请输入名称' }]}>
              <Input placeholder="例如 雨夜白噪音" />
            </Form.Item>
            <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择分类' }]}>
              <Select options={categoryOptions} />
            </Form.Item>
          </div>

          <div className="bgm-form-grid">
            <Form.Item label="授权" name="license_name">
              <Input placeholder="自有 / 已授权 / CC BY" />
            </Form.Item>
            <Form.Item label="作者" name="artist">
              <Input placeholder="作者或来源名称" />
            </Form.Item>
          </div>

          <div className="bgm-form-grid">
            <Form.Item label="时长（秒）" name="duration_seconds">
              <InputNumber min={0} max={86400} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="默认音量（%）" name="volume" rules={[{ required: true, message: '请输入默认音量' }]}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="bgm-form-grid">
            <Form.Item label="排序" name="sort" rules={[{ required: true, message: '请输入排序' }]}>
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
              <Select options={[{ label: '启用', value: 1 }, { label: '停用', value: 0 }]} />
            </Form.Item>
          </div>

          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="适用场景、使用建议" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BackgroundMusicPage;
